import { Flex, Spinner, useToast } from '@chakra-ui/react'
import { ApiClient } from '@storefronts/common-types'
import { useRouter } from 'next/router'
import { usePostHog } from 'posthog-js/react'
import { useContext, useEffect, useState } from 'react'
import { themes } from '../../styles/Theme'
import { R } from '../modules/api/actions'
import { AuthContext } from '../modules/auth/authContext'
import { unprotectedRoutes } from '../modules/auth/unprotectedRoutes'
import { SideNavigation } from './navigation/SideNavigation'
import { identifyChatlio } from './shared/Chatlio'
import { LocalStorageKey } from '@/lib/storageConstants'

export const AuthLayout: React.FunctionComponent<{
    children: React.ReactNode
}> = ({ children }) => {
    const router = useRouter()
    const authContext = useContext(AuthContext)
    const toast = useToast()
    const [isAuthSetup, setIsAuthSetup] = useState(false)
    const posthog = usePostHog()

    useEffect(() => {
        // Skip checks for unprotected routes
        if (unprotectedRoutes.includes(router.pathname)) {
            return
        }
    }, [router.pathname])

    // TODO: how to share this duplicate code with OnboardingLayout
    useEffect(() => {
        if (router.pathname == '/login') {
            return
        }
        // make profile request
        R.getProfile()
            .then(async (resp) => {
                if (resp.isAuthenticated) {
                    R.setClient(resp.user.client?.clientHash)
                    authContext.setLoggedIn(true)
                    authContext.setUser(resp.user)
                    if (
                        resp.user.username.includes('blackcrow.ai') ||
                        resp.user.username.includes('viddyapp.io')
                    ) {
                        authContext.setIsSuperUser(true)
                    }

                    // Check if user has any clients
                    if (!resp.user.client) {
                        router.push('/onboarding')
                    }

                    const clientInLocalStorage = window.localStorage.getItem(
                        LocalStorageKey.CLIENT_HASH,
                    )

                    let client: ApiClient
                    if (
                        !clientInLocalStorage ||
                        clientInLocalStorage === resp.user.client?.clientHash
                    ) {
                        // If no stored client or stored client is the user's primary client
                        client = resp.user.client
                    }

                    if (resp.user.client) {
                        // If persisted client is one of user's other clients
                        const allClientsResp = await R.getAvailableClients()
                        const allClients = allClientsResp.clients
                        const filteredClient = allClients.find(
                            (c) => c.clientHash === clientInLocalStorage,
                        )

                        if (filteredClient) {
                            // If persisted client is one of user's other clients
                            client = filteredClient
                        } else {
                            //Fallback
                            client = resp.user.client
                        }

                        window.localStorage.setItem(
                            LocalStorageKey.CLIENT_HASH,
                            client?.clientHash,
                        )
                        authContext.setClient(client)
                        R.setClient(client?.clientHash)

                        // Identify posthog
                        posthog.identify(resp.user.username, {
                            username: resp.user.username,
                            client: client?.name,
                        })

                        // Identify Chatlio
                        identifyChatlio(String(resp.user.id), {
                            name: resp.user.name,
                            email: resp.user.username,
                            client: client?.name,
                            clientHash: client?.clientHash,
                        })

                        // Check if we need to redirect to onboarding
                        if (
                            !unprotectedRoutes.includes(router.pathname) &&
                            client?.onboardingStep
                        ) {
                            router.push(
                                `/onboarding?step=${client.onboardingStep}`,
                            )
                        }
                    }

                    setIsAuthSetup(true)
                    return
                }

                if (
                    !unprotectedRoutes.includes(router.pathname) &&
                    (authContext.isLoggedIn === null ||
                        authContext.user === null)
                ) {
                    router.push('/login')
                }
            })
            .catch((err) => {
                console.error(err)
                router.push('/login')
                toast({
                    title: 'Unable to log you in.',
                    position: 'top',
                    description: '',
                    status: 'warning',
                    duration: 3000,
                    isClosable: true,
                })
            })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    if (!authContext.isLoggedIn) {
        return (
            <Flex
                minHeight={'100vh'}
                height="100%"
                width="100vw"
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Spinner color={themes.colors.brand.blue['500']} />
            </Flex>
        )
    }

    if (!isAuthSetup) {
        return (
            <Flex
                minHeight={'100vh'}
                height="100%"
                width="100vw"
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Spinner color={themes.colors.brand.blue['500']} />
            </Flex>
        )
    }

    return (
        <Flex
            fontFamily={themes.fonts.light}
            minHeight={'100vh'}
            width="100vw"
            backgroundColor="brand.background.secondary"
            flexDirection="column"
            position="relative"
        >
            <Flex width="100%" position="relative">
                <Flex
                    position="fixed"
                    top="0"
                    left="0"
                    bottom="0"
                    width={{ base: '100%', md: '16rem' }}
                    zIndex={{ base: 40, md: 10 }}
                    pointerEvents="auto"
                >
                    <SideNavigation />
                </Flex>
                <Flex
                    flex={5}
                    flexDirection="column"
                    backgroundColor={'#fff'}
                    pt={8}
                    pb={4}
                    marginLeft={{
                        base: '2',
                        md: router.pathname !== '/onboarding' ? '16rem' : '0',
                    }}
                    marginY={'1rem'}
                    pr={{ base: '2', md: '8' }}
                    pl={{ base: '2', md: '8' }}
                    width="100%"
                    height={{ base: 'auto', md: '100vh' }}
                    maxHeight={{ base: 'none', md: 'calc(100vh - 2rem)' }}
                    overflow={{ base: 'visible', md: 'auto' }}
                    borderRadius={{ base: '1rem', md: '1rem' }}
                >
                    {children}
                </Flex>
            </Flex>
        </Flex>
    )
}
